import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap, catchError } from 'rxjs';
import { throwError } from 'rxjs';
import {
  FixedMerchantData,
  ItinerantMerchantData,
  Service,
  CompleteOnboardingResponse,
  MerchantType,
  CompleteOnboardingPayload,
} from '../models/onboarding.model';
import type { ActivityType } from '../models/cluster.model';
import { AuthService } from './auth.service';
import { AnalyticsService } from './analytics.service';
import { LogService } from './log.service';

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private authService = inject(AuthService);
  private analytics = inject(AnalyticsService);
  private httpClient = inject(HttpClient);
  private log = inject(LogService);
  /*
  private apiUrl = 'http://analyticai.com:3000/api/onboarding';
  private merchantApiUrl = 'http://analyticai.com:3000/api/merchants';
  private servicesApiUrl = 'http://analyticai.com:3000/api/services';
  private eventsApiUrl = 'http://analyticai.com:3000/api/events';
*/
  private apiUrl = '/api/onboarding';
  private merchantApiUrl = '/api/merchants';
  private servicesApiUrl = '/api/services';
  private eventsApiUrl = '/api/events';

  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    if (token) {
      return new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });
    }
    return new HttpHeaders({ 'Content-Type': 'application/json' });
  }

  /**
   * ÉTAPE 2 : Soumettre le type d'activité
   */
  submitActivityType(activityType: ActivityType): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.httpClient
      .patch<any>(`${this.apiUrl}/step/1`, { activityType }, { headers })
      .pipe(
        tap((res) => {
          this.log.info('ActivityType submitted', { activityType });
          this.analytics.trackOnboardingStep('1_activite');
        }),
        catchError((err) => {
          this.log.error('Error submitting activityType', {
            error: (err as Error).message,
          });
          return throwError(() => err);
        }),
      );
  }

  submitLocationType(merchantType: MerchantType): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.httpClient
      .patch<any>(`${this.apiUrl}/step/2`, { merchantType }, { headers })
      .pipe(
        tap((res) => {
          this.log.info('Location type submitted', { merchantType });
          this.analytics.trackOnboardingStep('2_localisation');
        }),
        catchError((err) => {
          this.log.error('Error submitting location type', {
            error: (err as Error).message,
          });
          return throwError(() => err);
        }),
      );
  }

  /**
   * ÉTAPE 3-4 (FIXE) : Soumettre données contexte fixe
   */
  submitFixedMerchantData(data: FixedMerchantData): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.httpClient
      .patch<any>(`${this.apiUrl}/step/3-fixed`, data, { headers })
      .pipe(
        tap((res) => {
          this.log.info('Fixed merchant context submitted');
          this.analytics.trackOnboardingStep('3_contexte_fixe');
        }),
        catchError((err) => {
          this.log.error('Error submitting fixed merchant data', {
            error: (err as Error).message,
          });
          return throwError(() => err);
        }),
      );
  }

  /**
   * ÉTAPE 3 (ITINÉRANT) : Soumettre données contexte itinérant
   */
  submitItinerantMerchantData(data: ItinerantMerchantData): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.httpClient
      .patch<any>(`${this.apiUrl}/step/3-itinerant`, data, { headers })
      .pipe(
        tap((res) => {
          this.log.info('Itinerant merchant context submitted');
          this.analytics.trackOnboardingStep('3_contexte_itinerant');
        }),
        catchError((err) => {
          this.log.error('Error submitting itinerant merchant data', {
            error: (err as Error).message,
          });
          return throwError(() => err);
        }),
      );
  }

  /**
   * ÉTAPE 5 (FINALE) : Compléter l'onboarding
   * Crée aussi le premier service/événement
   */
  completeOnboarding(
    payload: CompleteOnboardingPayload,
  ): Observable<CompleteOnboardingResponse> {
    const headers = this.getAuthHeaders();

    this.log.info('Envoi completeOnboarding');

    return this.httpClient
      .post<CompleteOnboardingResponse>(`${this.apiUrl}/complete`, payload, {
        headers,
      })
      .pipe(
        tap((res) => {
          this.log.info('Onboarding completed');

          this.authService.setToken(res.token);
          this.authService.refreshCurrentUser();
          this.analytics.trackOnboardingComplete(
            res.merchant?.merchantType ?? payload.merchantType ?? 'unknown',
          );
        }),
        catchError((err) => {
          this.log.error('Error completing onboarding', {
            error: (err as Error).message,
          });
          return throwError(() => err);
        }),
      );
  }

  /**
   * Créer le premier service (FIXE)
   */
  createFirstService(
    service: Omit<Service, 'id' | 'createdAt'>,
  ): Observable<Service> {
    const headers = this.getAuthHeaders();
    return this.httpClient
      .post<Service>(`${this.servicesApiUrl}`, service, { headers })
      .pipe(
        tap((res) => this.log.info('First service created')),
        catchError((err) => {
          this.log.error('Error creating service', {
            error: (err as Error).message,
          });
          return throwError(() => err);
        }),
      );
  }

  /**
   * Créer le premier événement (ITINÉRANT)
   */
  createFirstEvent(event: Omit<Event, 'id' | 'createdAt'>): Observable<Event> {
    const headers = this.getAuthHeaders();
    return this.httpClient
      .post<Event>(`${this.eventsApiUrl}`, event, { headers })
      .pipe(
        tap((res) => this.log.info('First event created')),
        catchError((err) => {
          this.log.error('Error creating event', {
            error: (err as Error).message,
          });
          return throwError(() => err);
        }),
      );
  }

  /**
   * Sauvegarder les produits par défaut (optionnel)
   */
  initializeDefaultProducts(activityType: ActivityType): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.httpClient
      .post<any>(
        `${this.merchantApiUrl}/products/initialize`,
        { activityType },
        { headers },
      )
      .pipe(
        tap((res) => this.log.info('Default products initialized')),
        catchError((err) => {
          this.log.error('Error initializing products', {
            error: (err as Error).message,
          });
          return throwError(() => err);
        }),
      );
  }

  /**
   * DEPRECATED : Ancienne méthode pour compatibilité
   * À supprimer après migration
   */
  updateOnboardingStep(step: number, data: any): Observable<any> {
    const headers = this.getAuthHeaders();
    this.log.warn(
      'updateOnboardingStep is deprecated, use specific methods instead',
    );
    return this.httpClient.patch<any>(`${this.apiUrl}/step/${step}`, data, {
      headers,
    });
  }

  /**
   * Récupérer le statut et données d'onboarding depuis la DB
   * Permet de pré-remplir les formulaires si l'utilisateur revient
   */
  getOnboardingStatus(): Observable<OnboardingStatusResponse> {
    const headers = this.getAuthHeaders();
    return this.httpClient
      .get<OnboardingStatusResponse>(`${this.apiUrl}/status`, { headers })
      .pipe(
        tap((res) => this.log.debug('Onboarding status from DB')),
        catchError((err) => {
          this.log.error('Error fetching onboarding status', {
            error: (err as Error).message,
          });
          return throwError(() => err);
        }),
      );
  }
}

/**
 * Réponse du endpoint GET /api/onboarding/status
 */
export interface OnboardingStatusResponse {
  onboardingStep: number | null;
  onboardingCompleted: boolean;
  merchantType: string | null;
  activityType: string | null;
  positioning: string | null;
  coverage: string | null;
  teamSize: string | null;
  affluence: string | null;
  periodicity: string | null;
  services: string[];
  eventTypes: string[];
  businessName: string | null;
  locationCity: string | null;
  locationRegion: string | null;
}
